import type { ZodValidator } from '@tanstack/zod-form-adapter';

import { SingleOptionField, SingleOptionItem } from '@connected/form-fields/components/option';
import { Button } from '@connected/ui/components/button';
import { Popout } from '@connected/ui/components/popout';
import { useScreenSize } from '@connected/ui/hooks/use-screen-size';
import { cn } from '@connected/ui/lib/utils';
import { useForm } from '@tanstack/react-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { useFormTouched } from '@/hooks/use-form-touched';
import { showConfirm } from '@/stores/confirmer';

import type { ProjectVersionStatus } from '../utils/project-status-types';

import { useProjectStatusMutation } from '../hooks/use-project-status-mutation';
import { PROJECT_STATUS_OPTIONS } from '../utils/project-status-constants';

type StatusChangePopoutProps = {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    currentStatus: ProjectVersionStatus;
    projectVersionId: string;
    onSuccess: () => void;
    children: React.ReactNode;
};

export function StatusChangePopout({
    open,
    onOpenChange,
    currentStatus,
    projectVersionId,
    onSuccess,
    children,
}: StatusChangePopoutProps) {
    const { t } = useTranslation('projects');
    const { t: c } = useTranslation('common');
    const { isMobile } = useScreenSize();

    const mutation = useProjectStatusMutation(projectVersionId);

    const form = useForm<{ status: ProjectVersionStatus }, ZodValidator>({
        defaultValues: {
            status: currentStatus,
        },
        onSubmit: async ({ value }) => {
            await mutation.mutateAsync({
                body: {
                    ElectricalProjectVersionId: projectVersionId,
                    Status: value.status,
                },
            });

            form.reset();
            onSuccess();
            toast.success(t('status.changeSuccess'));
        },
    });

    const isTouched = useFormTouched(form.store, { status: currentStatus });

    async function handleOpenChange(nextOpen: boolean) {
        if (nextOpen) {
            onOpenChange(true);
            return;
        }

        if (!isTouched) {
            onOpenChange(false);
            return;
        }

        if (await showConfirm(t('status.confirmNotChange'))) {
            form.reset();
            onOpenChange(false);
        }
    }

    function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
        event.preventDefault();
        event.stopPropagation();
        form.handleSubmit();
    }

    return (
        <Popout open={open} onOpenChange={handleOpenChange}>
            {children}
            <Popout.Content
                className={cn('max-w-md', {
                    'max-w-none': isMobile,
                })}
            >
                <Popout.Header className="flex-col items-start">
                    <Popout.Title>{t('status.changeTitle')}</Popout.Title>
                    <Popout.Description>{t('status.changeDescription')}</Popout.Description>
                </Popout.Header>
                <Popout.Body className="gap-4 py-4" asChild>
                    <form onSubmit={handleSubmit} id="status-change-form">
                        <form.Field
                            name="status"
                            children={({ state, handleChange }) => (
                                <SingleOptionField
                                    options={PROJECT_STATUS_OPTIONS}
                                    state={state}
                                    handleChange={handleChange}
                                    render={SingleOptionItem}
                                />
                            )}
                        />
                    </form>
                </Popout.Body>
                <Popout.Footer>
                    <Button
                        variant="outline"
                        className="w-full"
                        disabled={mutation.isPending}
                        onClick={() => handleOpenChange(false)}
                    >
                        {c('actions.general.cancel')}
                    </Button>
                    <Button
                        className="w-full"
                        type="submit"
                        form="status-change-form"
                        disabled={mutation.isPending}
                    >
                        {t('status.changeButton')}
                    </Button>
                </Popout.Footer>
            </Popout.Content>
        </Popout>
    );
}
